// src-lit/components/product/kd-product-warning.ts
import { LitElement, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import { onEvent } from '../../utils/dom';
import { EVENT_NAMES, type VariantSelectedDetail } from '../../constants/events';
import { SELECTORS } from '../../constants/selectors';

interface WarningVariantEntry {
  /** 賞味期限警告文（current variant.option2 + days_left + inventory_quantity 由来。空文字なら非表示） */
  expirationWarning: string;
  /** 賞味期限ラベル（パントリー子商品向け、variant.option2 + expiration_text 由来。空文字なら非表示） */
  expirationLabel: string;
  /** パントリー子商品か（variant.metafields.variant.pantry_child_code 由来）。共通注意 <li> の出し分けに使う */
  isPantryChildProduct: boolean;
}

/**
 * @element kd-product-warning
 * @description variant 切替に応じて注意事項・警告を更新する。
 *   - 賞味期限警告 <li>: hidden + textContent
 *   - 賞味期限ラベル <li>（パントリー子商品用）: hidden + textContent
 *   - 共通注意 <li>（非パントリー子商品時のみ表示）: hidden トグル
 *   配送除外・酒類・クール便は product 単位の判定（product.tags / collections）なので variant 不変。
 */
@customElement('kd-product-warning')
export class KdProductWarning extends LitElement {
  createRenderRoot() {
    return this;
  }

  private unsubscribe: (() => void) | null = null;

  private variantMap: Record<string, WarningVariantEntry | undefined> = {};

  render() {
    return nothing;
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.loadVariantMap();
    this.unsubscribe = onEvent<VariantSelectedDetail>(
      document,
      EVENT_NAMES.PRODUCT.VARIANT_SELECTED,
      (e) => this.handleVariantSelected(e.detail),
    );
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.unsubscribe?.();
    this.unsubscribe = null;
  }

  private loadVariantMap(): void {
    const script = this.querySelector<HTMLScriptElement>(
      SELECTORS.PRODUCT_WARNING.VARIANT_MAP,
    );
    if (!script?.textContent) return;
    try {
      this.variantMap = JSON.parse(script.textContent);
    } catch {
      // JSON parse 失敗時は空マップのまま
    }
  }

  private handleVariantSelected(detail: VariantSelectedDetail): void {
    const entry = this.variantMap[detail.variantId];
    if (!entry) return;
    this.updateText(
      SELECTORS.PRODUCT_WARNING.EXPIRATION_WARNING,
      entry.expirationWarning,
    );
    this.updateText(
      SELECTORS.PRODUCT_WARNING.EXPIRATION_LABEL,
      entry.expirationLabel,
    );
    this.toggleAll(
      SELECTORS.PRODUCT_WARNING.NON_PANTRY_ONLY,
      !entry.isPantryChildProduct,
    );
  }

  private updateText(selector: string, value: string): void {
    const el = this.querySelector<HTMLElement>(selector);
    if (!el) return;
    if (value === '') {
      el.hidden = true;
      return;
    }
    el.hidden = false;
    el.textContent = value;
  }

  private toggleAll(selector: string, visible: boolean): void {
    this.querySelectorAll<HTMLElement>(selector).forEach((el) => {
      el.hidden = !visible;
    });
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'kd-product-warning': KdProductWarning;
  }
}
